import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from "@/components/ui/date-picker";
import { Clock, User, Calendar, AlertTriangle, Timer, Watch, Filter, X } from 'lucide-react';
import { format, parseISO, isValid, differenceInMinutes, startOfDay } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useSupabaseUser } from '@/hooks/use-supabase-user';

interface ProblematicClockIn {
  id: string;
  user_id: string;
  team_id: string;
  clock_in: string;
  clock_out?: string;
  status: string;
  shift_id?: string;
  scheduled_start_time?: string;
  minutes_late?: number;
  profiles?: {
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
}

export const ProblematicClockInsList = () => {
  const { isAdmin, isSuperAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  const [dateFilter, setDateFilter] = useState<Date | undefined>(undefined);
  const [teamFilter, setTeamFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const { data: problematicClockIns, isLoading } = useQuery({
    queryKey: ['problematic-clock-ins', user?.id, isAdmin, isSuperAdmin],
    queryFn: async () => {
      console.log('🔍 ProblematicClockIns - Starting query', { userId: user?.id, isAdmin, isSuperAdmin });
      
      if (!user?.id) {
        console.log('❌ No user ID');
        return [];
      }

      // Only admins and super admins can access this data
      if (!isAdmin && !isSuperAdmin) {
        console.log('❌ User is not admin or super admin');
        return [];
      }

      // Get clock-in records from the last 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      console.log('📅 Fetching clock-ins since:', thirtyDaysAgo.toISOString());

      let clockInQuery = supabase
        .from('clock_in_out')
        .select('*')
        .gte('clock_in', thirtyDaysAgo.toISOString())
        .order('clock_in', { ascending: false })
        .limit(200);

      // For regular admins, apply filtering
      if (isAdmin && !isSuperAdmin) {
        const { data: adminGroupMembers } = await supabase
          .from('admin_team')
          .select('member_id')
          .eq('admin_id', user.id)
          .in('member_type', ['parent_admin', 'contributor']);

        if (adminGroupMembers && adminGroupMembers.length > 0) {
          const adminIds = adminGroupMembers.map(m => m.member_id);
          
          const { data: guards } = await supabase
            .from('profiles')
            .select('id')
            .in('created_by', adminIds);

          if (guards && guards.length > 0) {
            const guardIds = guards.map(g => g.id);
            clockInQuery = clockInQuery.in('user_id', guardIds);
          }
        }
      }

      const { data: clockIns, error: clockInError } = await clockInQuery;

      if (clockInError) {
        console.error('❌ Clock-ins query error:', clockInError);
        throw clockInError;
      }
      
      console.log('✅ Clock-ins fetched:', clockIns?.length || 0);
      
      if (!clockIns || clockIns.length === 0) {
        console.log('⚠️ No clock-ins found');
        return [];
      }

      // Fetch profiles separately to avoid PostgREST schema cache issues
      const userIds = [...new Set(clockIns.map(ci => ci.user_id))];
      console.log('👥 Fetching profiles for', userIds.length, 'users');
      
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .in('id', userIds);

      console.log('✅ Profiles fetched:', profilesData?.length || 0);

      // Now check each clock-in against shift schedules
      const problematicRecords: ProblematicClockIn[] = [];
      console.log('🔍 Checking', Math.min(clockIns.length, 50), 'clock-ins against shifts...');

      for (let i = 0; i < Math.min(clockIns.length, 50); i++) {
        const clockIn = clockIns[i];
        const clockInTime = new Date(clockIn.clock_in);

        // Find shifts for this user on the same day
        const startOfDay = new Date(clockInTime);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(clockInTime);
        endOfDay.setHours(23, 59, 59, 999);

        const { data: shifts, error: shiftError } = await supabase
          .from('team_schedules')
          .select('*')
          .gte('start_date', startOfDay.toISOString())
          .lte('start_date', endOfDay.toISOString())
          .contains('assigned_guards', [clockIn.user_id]);

        if (shiftError) {
          console.error(`❌ Shift query error for clock-in ${i}:`, shiftError);
          continue;
        }
        
        if (!shifts || shifts.length === 0) {
          console.log(`⚠️ Clock-in ${i}: No shifts found for user ${clockIn.user_id} on ${clockInTime.toISOString()}`);
          continue;
        }
        
        console.log(`✅ Clock-in ${i}: Found ${shifts.length} shifts`);

        // Find the shift that this clock-in likely belongs to
        let matchingShift = null;
        let minutesLate = 0;

        for (const shift of shifts) {
          const shiftStart = fromLocalISOString(shift.start_date);
          const shiftEnd = fromLocalISOString(shift.end_date);
          const timeDiff = differenceInMinutes(clockInTime, shiftStart);

          console.log(`  📊 Shift ${shift.id}: Start ${shiftStart.toISOString()}, End ${shiftEnd.toISOString()}, Clock-in ${clockInTime.toISOString()}, Diff: ${timeDiff} min`);

          // Problematic if clocked in more than 15 minutes late, but before shift ends
          if (timeDiff > 15 && clockInTime <= shiftEnd) {
            matchingShift = shift;
            minutesLate = timeDiff;
            console.log(`  ⚠️ PROBLEMATIC: ${minutesLate} minutes late!`);
            break;
          } else if (timeDiff <= 15) {
            console.log(`  ✅ OK: ${timeDiff} minutes (within 15 min grace period)`);
          } else if (clockInTime > shiftEnd) {
            console.log(`  ⚠️ Clock-in after shift ended`);
          }
        }

        if (matchingShift) {
          problematicRecords.push({
            ...clockIn,
            shift_id: matchingShift.id,
            scheduled_start_time: matchingShift.start_date,
            minutes_late: minutesLate,
            profiles: profilesData?.find(p => p.id === clockIn.user_id) || null
          });
        }
      }

      console.log('✅ Final problematic records:', problematicRecords.length);

      // Sort by clock-in time (most recent first) and limit to 50
      return problematicRecords
        .sort((a, b) => new Date(b.clock_in).getTime() - new Date(a.clock_in).getTime())
        .slice(0, 50);
    },
    enabled: !!user?.id && (isAdmin || isSuperAdmin)
  });

  // Fetch teams for filter
  const { data: teams } = useQuery({
    queryKey: ['teams-for-filter', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  // Filter problematic clock-ins by date and team
  const filteredClockIns = useMemo(() => {
    if (!problematicClockIns) return [];

    return problematicClockIns.filter(clockIn => {
      // Date filter
      if (dateFilter) {
        const clockInDate = startOfDay(parseISO(clockIn.clock_in));
        const filterDate = startOfDay(dateFilter);
        if (clockInDate.getTime() !== filterDate.getTime()) {
          return false;
        }
      }

      // Team filter
      if (teamFilter && teamFilter !== 'all') {
        if (clockIn.team_id !== teamFilter) {
          return false;
        }
      }

      return true;
    });
  }, [problematicClockIns, dateFilter, teamFilter]);

  const clearFilters = () => {
    setDateFilter(undefined);
    setTeamFilter('all');
  };

  const hasActiveFilters = dateFilter || teamFilter !== 'all';

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'MMM dd, yyyy HH:mm');
      }
    } catch {
      // Fallback for invalid dates
    }
    return dateString;
  };

  const getSeverityColor = (minutesLate: number) => {
    if (minutesLate >= 60) {
      return 'bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400 border border-red-500/20';
    } else if (minutesLate >= 30) {
      return 'bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400 border border-orange-500/20';
    } else {
      return 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-500/20';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-lg font-medium text-muted-foreground">Loading problematic clock-ins...</p>
        </div>
      </div>
    );
  }

  if (!problematicClockIns?.length) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl">
              <Watch className="h-16 w-16 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">No Problematic Clock-Ins</h3>
              <p className="text-muted-foreground/70">No problematic clock-in records have been found.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <Card className="border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label htmlFor="date-filter">Filter by Date</Label>
                <DatePicker
                  date={dateFilter}
                  onSelect={setDateFilter}
                  placeholder="Pick a date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team-filter">Filter by Team</Label>
                <Select value={teamFilter} onValueChange={setTeamFilter}>
                  <SelectTrigger id="team-filter">
                    <SelectValue placeholder="All Teams" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams?.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <strong>{filteredClockIns.length}</strong> of <strong>{problematicClockIns.length}</strong> problematic clock-ins
        </p>
      </div>

      {/* Results Grid */}
      {filteredClockIns.length === 0 ? (
        <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-primary/20">
          <CardContent className="text-center py-16">
            <div className="flex flex-col items-center space-y-6">
              <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl">
                <Watch className="h-16 w-16 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-muted-foreground">No Results Found</h3>
                <p className="text-muted-foreground/70">No problematic clock-ins match your filters.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
          {filteredClockIns.map((clockIn, index) => (
        <Card
          key={clockIn.id}
          className={cn(
            "group transform transition-all duration-500 hover:scale-[1.02] hover:shadow-luxury",
            "bg-gradient-to-br from-background/95 via-background/90 to-muted/50 border-primary/20",
            "hover:border-primary/40 backdrop-blur-sm"
          )}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="relative overflow-hidden">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 group-hover:from-primary/10 group-hover:to-accent/10 transition-all duration-300"></div>

            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

            <div className="relative p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
                    clockIn.minutes_late && clockIn.minutes_late >= 60 ? 'bg-red-500 text-white' :
                    clockIn.minutes_late && clockIn.minutes_late >= 30 ? 'bg-orange-500 text-white' :
                    'bg-amber-500 text-white'
                  )}>
                    <Watch className="h-4 w-4" />
                  </div>

                  <Badge className={cn(
                    "font-medium",
                    clockIn.minutes_late ? getSeverityColor(clockIn.minutes_late) : getSeverityColor(30)
                  )}>
                    {clockIn.minutes_late || 0} min late
                  </Badge>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  {formatDateTime(clockIn.clock_in)}
                </div>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <User className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Guard</p>
                  <p className="text-sm font-semibold">
                    {clockIn.profiles?.full_name ||
                     `${clockIn.profiles?.first_name || ''} ${clockIn.profiles?.last_name || ''}`.trim() ||
                     'Unknown Guard'}
                  </p>
                </div>
              </div>

              {/* Time Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Timer className="h-4 w-4 text-primary" />
                  Clock In Details
                </h4>

                <div className="grid grid-cols-1 gap-3 p-4 bg-muted/20 rounded-xl">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Scheduled Start</p>
                      <p className="font-semibold">
                        {clockIn.scheduled_start_time ? formatDateTime(clockIn.scheduled_start_time) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Actual Clock In</p>
                      <p className="font-semibold">
                        {formatDateTime(clockIn.clock_in)}
                      </p>
                    </div>
                  </div>

                  {clockIn.shift_id && (
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">Shift ID</p>
                      <p className="font-semibold text-xs font-mono">{clockIn.shift_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <Badge variant={clockIn.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                  {clockIn.status}
                </Badge>
                {clockIn.clock_out && (
                  <span className="text-xs text-muted-foreground">
                    Clocked out: {formatDateTime(clockIn.clock_out)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}
        </div>
      )}
    </div>
  );
};
